import { HTTPClient } from './HTTPClient';

export class APIClient {

    static inject = () => [HTTPClient]
    constructor(private http: HTTPClient) {}

    private cachedEntryPromise: Promise<any> | undefined;

    entry = async () => {

        if (!this.cachedEntryPromise) {
            this.cachedEntryPromise = this.http.request({ url: '/', headers: { accept: 'application/vnd.innerhive.entry.v1+json' } })
                .then(({ data }) => data)
                .catch(e => {
                    this.cachedEntryPromise = undefined;
                    throw e;
                });
        }

        return this.cachedEntryPromise;
    };

    // Used by tests that request a re-fetch to see new links
    clearEntryCache = () => {
        this.cachedEntryPromise = undefined;
    };

    delete = async (resourceOrLink: any, params?: any) => {
        let url = this.getUrlFromResourceOrLink(resourceOrLink);
        if (params) {
            url = this.substituteParams(url, params);
        }
        const response = await this.http.request({ url, method: 'DELETE' });
        return response.data;
    };

    put = async (resourceOrLink: any, data?: any, headers?: any, params?: any) => {
        let url = this.getUrlFromResourceOrLink(resourceOrLink);
        url = this.substituteParams(url, params || data);

        // if no data is provided, assume resource contains data
        const dataOrResource = (!data && resourceOrLink.links) ? resourceOrLink : data;

        const response = await this.http.request({ url, data: dataOrResource, headers, method: 'PUT' });
        return response.data;
    };

    patch = async (resourceOrLink: any, data: any, headers?: any, params?: any) => {
        let url = this.getUrlFromResourceOrLink(resourceOrLink);
        url = this.substituteParams(url, params || data);
        const response = await this.http.request({ url, data, headers, method: 'PATCH' });
        return response.data;
    };

    post = async (resourceOrLink: any, data: any, headers?: any, params?: any) => {
        let url = this.getUrlFromResourceOrLink(resourceOrLink);
        url = this.substituteParams(url, params || data);
        const response = await this.http.request({ url, data, headers, method: 'POST' });
        return response.data;
    };

    get = async (resourceOrLink: any, accept?: any, params?: any, options?: any) => {
        const headers = accept ? { accept } : undefined;
        let url = this.getUrlFromResourceOrLink(resourceOrLink);
        url = this.substituteParams(url, params);
        const response = await this.http.request({ url, headers, method: 'GET', options });
        return response.data;
    };

    private getUrlFromResourceOrLink = (resourceOrLink: any) => {
        // if it's a string, assume it's the url
        if (typeof resourceOrLink === 'string') {
            return resourceOrLink;
        }
        // if it looks like a resource, return self link
        if (resourceOrLink.links && resourceOrLink.links.self){
            return resourceOrLink.links.self.href;
        }
        // if it looks like a link, return the url
        if (resourceOrLink.href){
            return resourceOrLink.href;
        }

        // otherwise throw
        throw new Error(`Could not find a url in ${JSON.stringify(resourceOrLink)}`);
    };

    private substituteParams = (url: string, params: any = {}) => {
        if (url.includes('[[')) {
            url = url.replace(/\[\[(.+?)\]\]/g, (_match, key) => {
                if (key.endsWith('?')) {
                    // substitute optional [[key?]] with the matching value from params or empty string
                    const name = key.slice(0, -1); // remove "?" from end
                    return encodeURIComponent(params[name] || '');
                } else {
                    // substitute required [[key]] with the matching value from params
                    if (!params[key]) {
                        throw new Error(`Missing required param "${key}"`);
                    }
                    return encodeURIComponent(params[key]);
                }
            });
        }
        return url;
    }
}
